import axios, { type InternalAxiosRequestConfig } from "axios";
import { getAccessToken } from "@privy-io/react-auth";
import { queryOptions } from "@tanstack/react-query";
import { Role } from "@/components/AuthorizationProvider.tsx";

export const apiUrl = import.meta.env.PROD
  ? import.meta.env.VITE_REACT_API_URL_PRODUCTION
  : import.meta.env.VITE_REACT_API_URL_DEVELOPMENT;

export const web3URL = import.meta.env.PROD
  ? import.meta.env.VITE_web3URL_API_URL_PRODUCTION
  : import.meta.env.VITE_web3URL_API_URL_DEVELOPMENT;

export const web3APIClient = axios.create({
  baseURL: web3URL,
  transitional: {
    // throw ETIMEDOUT error instead of generic ECONNABORTED on request timeouts
    clarifyTimeoutError: true,
  },
});

export const apiClient = axios.create({
  baseURL: apiUrl,
  transitional: {
    // throw ETIMEDOUT error instead of generic ECONNABORTED on request timeouts
    clarifyTimeoutError: true,
  },
});

// Add a request interceptor
const axiosRequestInterceptor = async (config: InternalAxiosRequestConfig) => {
  const token = await getAccessToken();
  try {
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  } catch {
    // untouched config if no session is present
    return config;
  }
};
apiClient.interceptors.request.use(axiosRequestInterceptor);
web3APIClient.interceptors.request.use(axiosRequestInterceptor);

export interface Tenant {
  tenant_name: string;
  company: string;
  logo_url: string;
  owner: string;
}

export const getAllTenantRecords = async (): Promise<Tenant[]> => {
  try {
    const response = await apiClient.get(`/api/tenants`);
    return response.data;
  } catch (error) {
    console.error("Error fetching tenants", error);
    throw error;
  }
};

export const createNewTenantRecord = async (data: any) => {
  try {
    const response = await apiClient.post(`/api/tenants`, data);
    return response.data;
  } catch (error) {
    console.error("Error creating tenant", error);
    throw error;
  }
};

export const updateExistingTenantRecord = async (tenant_data: any) => {
  try {
    const response = await apiClient.put(
      `/api/tenants/${tenant_data.tenant_name}`,
      tenant_data,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating tenant", error);
    throw error;
  }
};

export interface User {
  email: string;
  username: string;
  role: Role;
  usage_tier: string;
  qstarlabs_tenant: string;
  default_persona: string;
  personas: string[];
}

export const getAllAccessRecords = async (): Promise<User[]> => {
  try {
    const response = await apiClient.get(`/api/portal-access/get_all`);
    return response.data;
  } catch (error) {
    console.error("Error fetching access records: ", error);
    throw error;
  }
};

// Create a new access record
export const createNewAccessRecord = async (data: any) => {
  try {
    const response = await apiClient.post(`/api/portal-access`, data);
    return response.data;
  } catch (error) {
    console.error("Error creating new access record: ", error);
    throw error;
  }
};

// Update an existing access record
export const updateExistingAccessRecord = async (username: any, data: any) => {
  try {
    const response = await apiClient.put(
      `/api/portal-access/${username}`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating access record: ", error);
    throw error;
  }
};

export const deleteExistingAccessRecord = async (username: any) => {
  try {
    const response = await apiClient.delete(`/api/portal-access/${username}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting access record: ", error);
    throw error;
  }
};

export const getFeaturePermissions = async (
  feature: string,
): Promise<string[]> => {
  try {
    const response = await apiClient.get(
      `/api/portal-access/private-preview/${feature}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching feature permissions: ", error);
    throw error;
  }
};

export const featureQueries = {
  permissions: (feature: string) =>
    queryOptions({
      queryKey: ["feature-permissions", feature],
      queryFn: () => getFeaturePermissions(feature),
    }),
};

export type PersonaDocuments = {
  static_web_links: string[];
  dynamic_web_links: string[];
  google_drive_folders: string[];
  github_repos: string[];
  core_personality: string[]; // New field for core personality documents
  static_data: string[]; // New field for static knowledge base data
  live_data: string[]; // New field for live news sources
  tools: string[]; // New field for associated tools
  knowledge_graph_status: string;
  last_built?: string | null;
};

export const getDocuments = async (
  personaId: string,
  authenticated: boolean = true,
): Promise<PersonaDocuments> => {
  try {
    const url = authenticated
      ? `/api/documents/${personaId}`
      : `/api/documents/${personaId}/public`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const deletePersona = async (personaId: string) => {
  try {
    const response = await apiClient.delete(`/api/personas/${personaId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting agent:", error);
    throw error;
  }
};

export const createPersona = async (
  personaData: any,
  documents: any,
  qs_username: any,
  avatarPath: any,
) => {
  try {
    // Ensure all values in personaData are single line
    for (const key in personaData) {
      if (typeof personaData[key] === "string") {
        personaData[key] = personaData[key]
          .replace(/\n/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      }
    }
    const payload = { personaData, documents, qs_username, avatarPath };
    const response = await apiClient.post(`/api/personas`, payload);
    return response.data.persona;
  } catch (error) {
    console.error("Error creating persona:", error);
    throw error;
  }
};

export const updatePersona = async (
  personaId: string,
  personaData: any,
  documents: any,
  avatarPath: any = null,
) => {
  try {
    // Ensure all values in personaData are single line
    for (const key in personaData) {
      if (typeof personaData[key] === "string") {
        personaData[key] = personaData[key]
          .replace(/\n/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      }
    }

    // Log final payload before sending the request
    const payload = { personaData, documents, avatarPath };

    // Send the update request
    const response = await apiClient.put(`/api/personas/${personaId}`, payload);

    // Return updated persona
    return response.data.persona;
  } catch (error: any) {
    // Log detailed error information
    console.error("Error updating agent:", error.message);
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
      console.error("Error response headers:", error.response.headers);
    } else if (error.request) {
      console.error("Error request data:", error.request);
    }
    throw error;
  }
};

export const uploadAvatar = async (file: any) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post(`/api/upload-avatar`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading avatar:", error);
    throw error;
  }
};

export const uploadImage = async (file: any, persona_id: string) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("persona_id", persona_id);
    const response = await apiClient.post(`/api/upload-image`, formData);
    return response.data;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
};

export const getAuthorization = async () => {
  try {
    const response = await apiClient.get(`/api/portal-access/authorization`);

    return response.data;
  } catch (error) {
    console.error("Get authorization failed:", error);
    throw error;
  }
};

export const canEditPersona = async (personaId: string) => {
  try {
    const response = await apiClient.get(
      `/api/portal-access/permissions/${personaId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error checking edit permissions:", error);
    throw error;
  }
};

export const adminQueries = {
  tenants: () =>
    queryOptions({
      queryKey: ["tenants"],
      queryFn: () => getAllTenantRecords(),
    }),
  users: () =>
    queryOptions({
      queryKey: ["users"],
      queryFn: () => getAllAccessRecords(),
    }),
};

export const agentQueries = {
  agent: (personaId: string) =>
    queryOptions({
      queryKey: ["agents", personaId],
      queryFn: () => canEditPersona(personaId),
      enabled: !!personaId,
    }),
};
